import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClipboardList, Eye, Plus, Trash2 } from 'lucide-react';
import { GuidedEmpty } from '../components/GuidedEmpty';
import { ModuleHero } from '../components/ModuleHero';
import { useStore } from '../hooks/useStore';
import { paths } from '../lib/paths';
import { normalizeSeasonSettings, updateTournamentSettings } from '../services/tournamentService';
import { newId } from '../services/ids';
import type { Registration, RegistrationCustomQuestion, RegistrationQuestionType, Tournament } from '../models';

/**
 * Registration overview — every event's registration window, at a glance.
 *
 * For each tournament/season we show: total registrants, status breakdown,
 * gross revenue collected, public registration link, and a quick path into
 * its workspace. Built on the existing Registration + PaymentIntent models.
 */

export function RegistrationOverviewPage() {
  const db = useStore((d) => d);
  const [searchParams] = useSearchParams();
  const requestedEventId = searchParams.get('event') ?? '';
  const [selectedEventId, setSelectedEventId] = useState(requestedEventId);
  const [newQuestion, setNewQuestion] = useState('');

  useEffect(() => {
    if (requestedEventId) setSelectedEventId(requestedEventId);
  }, [requestedEventId]);

  const events = useMemo(() => {
    const regsByTournament = new Map<string, Registration[]>();
    for (const r of db.registrations ?? []) {
      const arr = regsByTournament.get(r.tournamentId) ?? [];
      arr.push(r);
      regsByTournament.set(r.tournamentId, arr);
    }

    const piByTournament = new Map<string, number>();
    for (const p of db.paymentIntents ?? []) {
      if (!p.tournamentId || p.status !== 'succeeded') continue;
      piByTournament.set(p.tournamentId, (piByTournament.get(p.tournamentId) ?? 0) + p.amountCents);
    }

    return [...db.tournaments]
      .filter((t) => t.status !== 'archived')
      .sort((a, b) => b.startsAt.localeCompare(a.startsAt))
      .map((t) => {
        const regs = regsByTournament.get(t.id) ?? [];
        return {
          tournament: t,
          regs,
          counts: {
            total: regs.length,
            approved: regs.filter((r) => r.status === 'approved').length,
            pending: regs.filter(
              (r) =>
                r.status === 'pending_review' ||
                r.status === 'pending_payment' ||
                r.status === 'pending_waiver',
            ).length,
            declined: regs.filter((r) => r.status === 'declined' || r.status === 'withdrawn').length,
          },
          revenueCents: piByTournament.get(t.id) ?? 0,
        };
      });
  }, [db]);

  const totals = useMemo(
    () =>
      events.reduce(
        (acc, e) => ({
          regs: acc.regs + e.counts.total,
          approved: acc.approved + e.counts.approved,
          pending: acc.pending + e.counts.pending,
          revenueCents: acc.revenueCents + e.revenueCents,
        }),
        { regs: 0, approved: 0, pending: 0, revenueCents: 0 },
      ),
    [events],
  );

  const dollars = (cents: number) =>
    `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const selectedEvent =
    events.find(
      (event) =>
        event.tournament.id === selectedEventId ||
        event.tournament.slug === selectedEventId,
    )?.tournament ?? events[0]?.tournament;

  return (
    <>
      <ModuleHero
        eyebrow="Run the league"
        title="Registration"
        description="Every event's sign-up window — registrants, waivers, payments, status."
        icon={ClipboardList}
        accent="arc"
      />

      <div className="grid grid--4" style={{ marginTop: 16 }}>
        <Stat label="Open registrations" value={String(totals.regs)} hint={`${totals.approved} approved`} />
        <Stat
          label="Needs review"
          value={String(totals.pending)}
          hint="Pending payment, waiver, or admin"
          tone={totals.pending > 0 ? 'warn' : undefined}
        />
        <Stat label="Gross collected" value={dollars(totals.revenueCents)} hint="Across all events" />
        <Stat label="Active events" value={String(events.length)} hint="Open in the registration tab" />
      </div>

      {events.length === 0 ? (
        <div style={{ marginTop: 16 }}>
          <GuidedEmpty
            icon={ClipboardList}
            title="No event to register for yet"
            body="Registration is configured per season or tournament. Create an event first, then open this page to turn on intake, waivers, fees, and custom questions."
            action={{ to: paths.adminSeasonNew, label: 'Create season' }}
            secondaryAction={{ to: paths.adminTournamentNew, label: 'Create tournament' }}
          />
        </div>
      ) : selectedEvent ? (
        <RegistrationBuilder
          tournament={selectedEvent}
          tournaments={events.map((event) => event.tournament)}
          waiverDocuments={db.waiverDocuments}
          newQuestion={newQuestion}
          onNewQuestion={setNewQuestion}
          onSelect={setSelectedEventId}
        />
      ) : null}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card__head">
          <div>
            <h3 className="card__title">How registration flows</h3>
            <p className="card__sub">
              Public link → form → waiver → payment → admin review → roster.
            </p>
          </div>
        </div>
        <ol className="m-bullets" style={{ marginTop: 8 }}>
          <li><strong>You publish</strong> a registration link from the event workspace.</li>
          <li><strong>Players sign up</strong> at <code>/register/{`{event-slug}`}</code>: fill the form, sign the waiver, pay the fee.</li>
          <li><strong>Payments are Stripe-shaped</strong> — currently mocked, ready to swap for live Stripe in one place.</li>
          <li><strong>You review</strong> pending submissions here or inside the event's roster page, then approve or waitlist.</li>
          <li><strong>Approved registrants</strong> become roster entries automatically.</li>
        </ol>
      </div>
    </>
  );
}

function RegistrationBuilder({
  tournament,
  tournaments,
  waiverDocuments,
  newQuestion,
  onNewQuestion,
  onSelect,
}: {
  tournament: Tournament;
  tournaments: Tournament[];
  waiverDocuments: Array<{ id: string; title: string }>;
  newQuestion: string;
  onNewQuestion: (value: string) => void;
  onSelect: (eventId: string) => void;
}) {
  const season = normalizeSeasonSettings(tournament.settings.season);
  const registration = season.registration;
  const requiredFields = new Set(registration.requiredFields ?? ['name', 'email']);

  const persistRegistration = (patch: Partial<typeof registration>) => {
    updateTournamentSettings(tournament.id, {
      season: {
        ...season,
        rosterIntake: patch.enabled ? 'registration' : season.rosterIntake,
        registration: { ...registration, ...patch },
      },
    });
  };

  const toggleRequired = (field: 'name' | 'email' | 'school', enabled: boolean) => {
    const next = new Set(requiredFields);
    if (enabled) next.add(field);
    else next.delete(field);
    persistRegistration({ requiredFields: Array.from(next) as Array<'name' | 'email' | 'school'> });
  };

  const addQuestion = () => {
    const label = newQuestion.trim();
    if (!label) return;
    const question: RegistrationCustomQuestion = {
      id: newId('regq'),
      label,
      type: 'text',
      required: false,
    };
    persistRegistration({ customQuestions: [...(registration.customQuestions ?? []), question] });
    onNewQuestion('');
  };

  const updateQuestion = (id: string, patch: Partial<RegistrationCustomQuestion>) => {
    persistRegistration({
      customQuestions: (registration.customQuestions ?? []).map((question) =>
        question.id === id ? { ...question, ...patch } : question,
      ),
    });
  };

  const removeQuestion = (id: string) => {
    persistRegistration({
      customQuestions: (registration.customQuestions ?? []).filter((question) => question.id !== id),
    });
  };

  return (
    <div className="card card--accent" style={{ marginTop: 16 }}>
      <div className="card__head">
        <div>
          <h3 className="card__title">Registration builder</h3>
          <p className="card__sub">Configure intake, fee, waiver, required fields, and custom questions for a real event.</p>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <select className="select" value={tournament.id} onChange={(event) => onSelect(event.target.value)} style={{ maxWidth: 260 }}>
            {tournaments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <Link to={paths.register(tournament.slug)} className="btn btn--ghost btn--sm">
            <Eye size={13} /> Preview form
          </Link>
        </div>
      </div>

      <div className="grid grid--2">
        <div className="stack">
          <label className="row" style={{ gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={registration.enabled} onChange={(event) => persistRegistration({ enabled: event.target.checked })} />
            <span><strong>Registration open</strong></span>
          </label>
          <label>
            Intake type
            <select className="select" value={registration.intakeType} onChange={(event) => persistRegistration({ intakeType: event.target.value as typeof registration.intakeType })}>
              <option value="individual">Individuals</option>
              <option value="team">Teams</option>
              <option value="both">Teams and individuals</option>
            </select>
          </label>
          <label>
            Fee
            <input
              className="input"
              type="number"
              min={0}
              step={5}
              value={Math.round(registration.feeCents / 100)}
              onChange={(event) => persistRegistration({ feeCents: Math.max(0, Number(event.target.value || 0) * 100) })}
            />
          </label>
          <div className="grid grid--2">
            <label>
              Opens
              <input className="input" type="datetime-local" value={toLocalInput(registration.opensAt)} onChange={(event) => persistRegistration({ opensAt: fromLocalInput(event.target.value) })} />
            </label>
            <label>
              Closes
              <input className="input" type="datetime-local" value={toLocalInput(registration.closesAt)} onChange={(event) => persistRegistration({ closesAt: fromLocalInput(event.target.value) })} />
            </label>
          </div>
          <label className="row" style={{ gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={registration.autoApprove} onChange={(event) => persistRegistration({ autoApprove: event.target.checked })} />
            <span>Auto-approve registrations after waiver/payment</span>
          </label>
        </div>

        <div className="stack">
          <label className="row" style={{ gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={registration.requireWaiver} onChange={(event) => persistRegistration({ requireWaiver: event.target.checked })} />
            <span><strong>Require waiver signature</strong></span>
          </label>
          <label>
            Waiver template
            <select className="select" value={registration.waiverDocumentId ?? ''} onChange={(event) => persistRegistration({ waiverDocumentId: event.target.value || undefined })} disabled={!registration.requireWaiver}>
              <option value="">Default event waiver</option>
              {waiverDocuments.map((document) => <option key={document.id} value={document.id}>{document.title}</option>)}
            </select>
          </label>
          <div className="card">
            <div className="eyebrow">Required core fields</div>
            {(['name', 'email', 'school'] as const).map((field) => (
              <label key={field} className="row" style={{ gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input type="checkbox" checked={requiredFields.has(field)} onChange={(event) => toggleRequired(field, event.target.checked)} />
                <span>{field === 'school' ? 'School or program' : field[0].toUpperCase() + field.slice(1)}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card__head">
          <div>
            <h3 className="card__title">Custom questions</h3>
            <p className="card__sub">Answers are stored on the registration record as form data.</p>
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input className="input" value={newQuestion} onChange={(event) => onNewQuestion(event.target.value)} placeholder="Question label, e.g. Jersey size" style={{ maxWidth: 360 }} />
          <button className="btn btn--primary btn--sm" type="button" onClick={addQuestion}>
            <Plus size={13} /> Add question
          </button>
        </div>
        {(registration.customQuestions ?? []).length === 0 ? (
          <div className="muted" style={{ marginTop: 12, fontSize: 13 }}>No custom questions yet.</div>
        ) : (
          <div className="stack" style={{ marginTop: 12 }}>
            {(registration.customQuestions ?? []).map((question) => (
              <div key={question.id} className="game-op-row">
                <div className="grid grid--3">
                  <input className="input" value={question.label} onChange={(event) => updateQuestion(question.id, { label: event.target.value })} />
                  <select className="select" value={question.type} onChange={(event) => updateQuestion(question.id, { type: event.target.value as RegistrationQuestionType })}>
                    <option value="text">Short text</option>
                    <option value="textarea">Long text</option>
                    <option value="select">Select</option>
                    <option value="checkbox">Checkbox</option>
                  </select>
                  <label className="row" style={{ gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={question.required} onChange={(event) => updateQuestion(question.id, { required: event.target.checked })} />
                    Required
                  </label>
                  {question.type === 'select' && (
                    <input
                      className="input"
                      value={(question.options ?? []).join(', ')}
                      onChange={(event) => updateQuestion(question.id, { options: event.target.value.split(',').map((value) => value.trim()).filter(Boolean) })}
                      placeholder="Options, comma-separated"
                      style={{ gridColumn: '1 / -1' }}
                    />
                  )}
                </div>
                <button className="btn btn--ghost btn--sm" type="button" onClick={() => removeQuestion(question.id)} aria-label="Remove question">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function toLocalInput(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
}

function fromLocalInput(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(value).toISOString();
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'warn' | 'ok';
}) {
  const color = tone === 'warn' ? 'var(--warn)' : tone === 'ok' ? 'var(--ok)' : 'var(--text)';
  return (
    <div className="card">
      <div className="eyebrow">{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, color }}>{value}</div>
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{hint}</div>
    </div>
  );
}
